// NOTE:
// This utility was originally written for Next.js (using next/config and next/router),
// but this project is a plain React app. We've rewritten it to avoid any Next.js
// dependencies and to work purely in the browser.
//
// DOMAIN is taken from REACT_APP_DOMAIN if provided, otherwise we fall back
// to the current hostname at runtime.
const DOMAIN =
  (typeof process !== "undefined" && process.env.REACT_APP_DOMAIN) ||
  (typeof window !== "undefined" ? window.location.hostname : "localhost");
const MAX_COOKIE_SIZE = 4000; // Each cookie has a ~4KB limit

/**
 * Retrieves and reconstructs the authentication token from multiple cookies.
 * Returns null if missing or invalid.
 */
export function getToken(): string | null {
  if (typeof window === "undefined") return null;

  let tokenParts: string[] = [];
  let partIndex = 1;

  // Collect all token parts (authToken1, authToken2, ...)
  while (true) {
    const tokenPart = getCookie(`d_c_c_t_p_${partIndex}`);
    if (!tokenPart) break;
    tokenParts.push(tokenPart);
    partIndex++;
  }

  if (tokenParts.length === 0) {
    redirectToLogin();
    return null;
  }

  const token = decodeURIComponent(tokenParts.join(""));
  //console.log("Token:", token);
  return token;
}

/**
 * Splits and stores the authentication token across multiple cookies.
 */
export function setAuthToken(token: string) {
  if (typeof window === "undefined") return;

  clearAuthToken(); // Ensure no old tokens remain

  const encodedToken = encodeURIComponent(token);
  const tokenParts = splitString(encodedToken, MAX_COOKIE_SIZE);
  
  tokenParts.forEach((part, index) => {
    document.cookie = `d_c_c_t_p_${index + 1}=${part}; domain=${DOMAIN}; path=/; secure; samesite=strict;`;
  });
}

/**
 * Clears all authentication token parts from cookies.
 */
export function clearAuthToken() {
  let partIndex = 1;

  while (getCookie(`d_c_c_t_p_${partIndex}`)) {
    document.cookie = `d_c_c_t_p_${partIndex}=; domain=${DOMAIN}; path=/; secure; samesite=strict; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    partIndex++;
  }
}

/**
 * Redirects user to login page if not authenticated.
 */
export function redirectToLogin() {
  if (typeof window !== "undefined") {
    // Account for basename in pathname check
    const pathname = window.location.pathname;
    const basename = "/compliance";
    const relativePath = pathname.startsWith(basename) ? pathname.slice(basename.length) : pathname;
    
    if (relativePath !== "/login" && relativePath !== "/error/1" && relativePath !== "/error/2") {
      // In a plain React app we don't have Next.js Router,
      // so we use a simple location change with basename.
      window.location.href = `${basename}/login`;
    } else {
      
    }
  }
}

/**
 * Splits a long string into smaller parts.
 */
function splitString(str: string, size: number): string[] {
  const result: string[] = [];
  for (let i = 0; i < str.length; i += size) {
    result.push(str.substring(i, i + size));
  }
  return result;
}

/**
 * Helper to get a cookie value by name.
 */
function getCookie(name: string): any {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}
